import { InlineError } from "@/components/QueryState";
import { api, type CodeHostConnection, type ContainerRow } from "@/lib/api";
import { codeHostsKey, instanceLabel, repoNoun, useCodeHosts } from "@/lib/code-host";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ExternalLink, Loader2, Server } from "lucide-react";
import type { ReactNode } from "react";
import { radioGroupKeyDown } from "@/lib/radio-keys";
import { HostIcon } from "@/components/HostIcon";

// Where the source of the apps this Boo builds goes by default: one of its
// code host connections (a private repository per app) or Boo-hosted. Each
// install can still pick otherwise.
export function AppSourceDefault({ container, canEdit }: { container: ContainerRow; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { data, active, defaultConnection } = useCodeHosts(container.id);
  const update = useMutation({
    mutationFn: (connectionId: string | null) => api.codeHosts.setDefault(container.id, connectionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: codeHostsKey(container.id) }),
  });

  if (!data) return null;
  const current = defaultConnection?.id ?? null;
  const pending = update.isPending ? (update.variables ?? null) : undefined;

  return (
    <section className="flex flex-col gap-3">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-balance">Where Boo saves app code</h2>
        <p className="text-sm text-muted-foreground text-pretty">
          {active.length
            ? "New apps this Boo builds save their code here. You can still choose per app when you add it."
            : "Boo saves the code of the apps it builds for you, or in your own repositories once you connect them."}
        </p>
      </div>

      {active.length === 0 && (
        <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
          <Server className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Saved by Boo</span>
            <span className="block text-xs text-muted-foreground text-pretty">Boo keeps the code for you.</span>
          </span>
          <Link
            to="/orgs/$orgId/containers/$containerId/general"
            params={{ orgId: container.organizationId, containerId: container.id }}
            className="shrink-0 text-sm font-medium underline underline-offset-2"
          >
            Connect GitHub or GitLab
          </Link>
        </div>
      )}

      {active.length > 0 && (
        <div role="radiogroup" aria-label="Where Boo saves app code" className="divide-y overflow-hidden rounded-xl border bg-card" onKeyDown={radioGroupKeyDown}>
          {active.map((conn) => (
            <SourceOption
              key={conn.id}
              selected={current === conn.id}
              pending={pending === conn.id}
              disabled={!canEdit || update.isPending}
              onSelect={() => update.mutate(conn.id)}
              icon={<HostIcon host={conn.host} className="size-4" />}
              title={conn.namespace}
              description={`A private ${repoNoun(conn.host)} per app${instanceLabel(conn) ? ` on ${instanceLabel(conn)}` : ""}.`}
            />
          ))}
          <SourceOption
            selected={current === null}
            pending={pending === null}
            disabled={!canEdit || update.isPending}
            onSelect={() => update.mutate(null)}
            icon={<Server className="size-4 text-muted-foreground" />}
            title="Saved by Boo"
            description="Boo keeps the code for you."
          />
        </div>
      )}
      {defaultConnection?.host === "github" && defaultConnection.repositorySelection === "selected" && (
        <SelectedReposWarning manageUrl={defaultConnection.manageUrl} />
      )}
      {active.length > 0 && !canEdit && <p className="text-xs text-muted-foreground">Only this Boo's admins can change this.</p>}
      {update.isError && <InlineError error={update.error} />}
    </section>
  );
}

function SourceOption({
  selected,
  pending,
  disabled,
  onSelect,
  icon,
  title,
  description,
}: {
  selected: boolean;
  pending: boolean;
  disabled: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled && !selected}
      onClick={() => !selected && onSelect()}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left outline-none transition-colors focus-visible:bg-muted/40",
        selected ? "bg-muted/40" : "hover:bg-muted/30",
        disabled && !selected && "cursor-not-allowed opacity-60 hover:bg-transparent",
      )}
    >
      <span className={cn("mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors", selected ? "border-foreground" : "border-muted-foreground/40")}>
        {pending ? <Loader2 className="size-3 animate-spin" /> : <span className={cn("size-2 rounded-full bg-foreground transition-transform", selected ? "scale-100" : "scale-0")} />}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-medium">
          {icon}
          <span className="truncate">{title}</span>
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground text-pretty">{description}</span>
      </span>
    </button>
  );
}

// GitHub doesn't add repositories the app creates to an installation limited
// to selected repositories, so each new app's repository must be added by hand.
export function SelectedReposWarning({ manageUrl }: { manageUrl: string | null }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-1">
        <p className="text-pretty">
          The Boo GitHub App can only reach selected repositories, so each repository Boo creates has to be added to it on GitHub before the app can use it.
          Giving it access to all repositories avoids this.
        </p>
        {manageUrl && (
          <a href={manageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium underline underline-offset-2">
            Change repository access on GitHub <ExternalLink className="size-3" />
          </a>
        )}
      </div>
    </div>
  );
}

export type { CodeHostConnection };
